/**
 * MapActionBar — Shared bottom action bar for all three map dashboards
 * (SubDashboard, ContractorDashboard, ProjectMapPage admin)
 *
 * Design: frosted glass panel, 64px status buttons, drag handle, safe area insets.
 * Handles both single-segment detail and batch selection display.
 */
import React from 'react';
import { Loader, X } from 'lucide-react';

// Unified status color palette (same everywhere)
export const MAP_STATUS_COLORS = {
  NOT_STARTED: '#9E9E9E',
  IN_PROGRESS: '#FFB800',
  COMPLETE: '#4CAF50',
  QC_APPROVED: '#2196F3',
  ISSUE: '#FF5722',
  SELECTED: '#FFD700',
};

// Resolve a status string to its color
export const getMapStatusColor = (status) => {
  switch (status) {
    case 'Complete': case 'QC Approved': return MAP_STATUS_COLORS.COMPLETE;
    case 'In Progress': return MAP_STATUS_COLORS.IN_PROGRESS;
    case 'Issue': return MAP_STATUS_COLORS.ISSUE;
    default: return MAP_STATUS_COLORS.NOT_STARTED;
  }
};

/**
 * MapActionBar
 *
 * Props:
 *   darkMode          - boolean
 *   selectedSegment   - single segment object (or null)
 *   selectedCount     - number of batch-selected items (0 when single)
 *   statusField       - which field holds the status (e.g. 'boring_status')
 *   onStatusUpdate    - (status: string) => void — called for single or batch
 *   onClear           - () => void — clear selection
 *   onClose           - () => void — close single-segment detail (optional, falls back to onClear)
 *   updating          - boolean — disable buttons while updating
 *   children          - extra content rendered between info and status buttons (e.g. section assign)
 *   extraInfo         - string or JSX shown after segment details row
 */
const MapActionBar = ({
  darkMode,
  selectedSegment,
  selectedCount = 0,
  statusField = 'boring_status',
  onStatusUpdate,
  onClear,
  onClose,
  updating = false,
  children,
  extraInfo,
}) => {
  const isBatch = selectedCount > 0;
  const hasContent = isBatch || selectedSegment;
  if (!hasContent) return null;

  const panelBg = darkMode ? 'rgba(20,20,22,0.97)' : 'rgba(255,255,255,0.98)';
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  const mutedColor = darkMode ? 'rgba(255,255,255,0.55)' : '#6b7280';
  const borderColor = darkMode ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)';

  const statusButtons = [
    { label: 'In Progress', status: 'In Progress', color: MAP_STATUS_COLORS.IN_PROGRESS, icon: '\u25B6' },
    { label: 'Complete', status: 'Complete', color: MAP_STATUS_COLORS.COMPLETE, icon: '\u2713' },
    { label: 'Issue', status: 'Issue', color: MAP_STATUS_COLORS.ISSUE, icon: '!' },
  ];

  const currentStatus = selectedSegment ? (selectedSegment[statusField] || 'Not Started') : null;

  return (
    <div style={{
      position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20,
      backgroundColor: panelBg,
      borderRadius: '24px 24px 0 0',
      boxShadow: '0 -8px 32px rgba(0,0,0,0.2)',
      backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
      padding: '20px 20px calc(20px + env(safe-area-inset-bottom)) 20px',
    }}>
      {/* Drag handle */}
      <div style={{
        width: '48px', height: '5px', borderRadius: '3px',
        backgroundColor: mutedColor, opacity: 0.3,
        margin: '0 auto 16px',
      }} />

      {/* Single segment detail */}
      {selectedSegment && !isBatch && (
        <>
          <button onClick={onClose || onClear} style={{
            position: 'absolute', top: '16px', right: '20px',
            background: 'none', border: 'none', color: mutedColor,
            cursor: 'pointer', padding: '8px', minWidth: '44px', minHeight: '44px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><X size={20} /></button>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '1.15rem', fontWeight: 700, color: textColor, marginBottom: '4px' }}>
              {selectedSegment.segment_id || selectedSegment.contractor_id || 'Segment'}
            </div>
            <div style={{
              fontSize: '0.85rem', color: mutedColor,
              display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center',
            }}>
              {(selectedSegment.street_name || selectedSegment.street) && (
                <span>{selectedSegment.street_name || selectedSegment.street}</span>
              )}
              {(selectedSegment.street_name || selectedSegment.street) && <span style={{ color: borderColor }}>·</span>}
              <span>{selectedSegment.footage || '?'} LF</span>
              <span style={{ color: borderColor }}>·</span>
              <span style={{
                display: 'inline-flex', alignItems: 'center', gap: '4px', fontWeight: 600,
              }}>
                <span style={{
                  width: '7px', height: '7px', borderRadius: '50%',
                  backgroundColor: getMapStatusColor(currentStatus),
                  display: 'inline-block',
                }} />
                <span style={{ color: getMapStatusColor(currentStatus) }}>
                  {currentStatus}
                </span>
              </span>
            </div>
          </div>

          {extraInfo && <div style={{ marginBottom: '16px' }}>{extraInfo}</div>}
        </>
      )}

      {/* Batch selection header */}
      {isBatch && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '16px',
        }}>
          <span style={{ fontSize: '1rem', fontWeight: 700, color: textColor }}>
            {selectedCount} segment{selectedCount !== 1 ? 's' : ''} selected
          </span>
          <button onClick={onClear} style={{
            background: 'none', border: `1px solid ${borderColor}`,
            borderRadius: '10px', padding: '8px 16px', color: mutedColor,
            fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
            minHeight: '40px',
          }}>Clear</button>
        </div>
      )}

      {/* Extra children (e.g. section assignment for contractor) */}
      {children}

      {/* Status action buttons — 64px, full color fill */}
      <div style={{ display: 'flex', gap: '10px' }}>
        {statusButtons.map(btn => {
          const isActive = !isBatch && currentStatus === btn.status;
          return (
            <button
              key={btn.status}
              disabled={updating || isActive}
              onClick={() => onStatusUpdate(btn.status)}
              style={{
                flex: 1, height: '64px', borderRadius: '16px', border: 'none',
                fontSize: '0.95rem', fontWeight: 800,
                cursor: updating || isActive ? 'default' : 'pointer',
                backgroundColor: isBatch ? btn.color : (isActive ? btn.color : `${btn.color}18`),
                color: isBatch ? '#fff' : (isActive ? '#fff' : btn.color),
                opacity: updating ? 0.6 : 1,
                transition: 'all 0.2s',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: 'center', gap: '4px',
              }}
            >
              {updating && isBatch ? (
                <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                <span style={{ fontSize: '1.4rem', lineHeight: 1 }}>{btn.icon}</span>
              )}
              <span>{updating && isBatch ? 'Updating...' : btn.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

/**
 * MapStatsBar — Top-center frosted glass pill showing segment counts
 *
 * Props:
 *   darkMode, total, done, active, issues (optional), selectedCount (optional)
 *   style - extra positioning styles
 */
export const MapStatsBar = ({ darkMode, total, done, active, issues, selectedCount, style }) => {
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  return (
    <div style={{
      position: 'absolute', top: '12px', left: '50%', transform: 'translateX(-50%)',
      zIndex: 10, display: 'flex', gap: '8px', padding: '8px 14px',
      alignItems: 'center',
      backgroundColor: darkMode ? 'rgba(20,20,22,0.92)' : 'rgba(255,255,255,0.95)',
      borderRadius: '12px', boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      fontSize: '0.8rem', fontWeight: 600,
      ...style,
    }}>
      <span style={{ color: textColor }}>{total} total</span>
      <span style={{ color: '#9E9E9E' }}>|</span>
      <span style={{ color: MAP_STATUS_COLORS.COMPLETE }}>{done} done</span>
      <span style={{ color: '#9E9E9E' }}>|</span>
      <span style={{ color: MAP_STATUS_COLORS.IN_PROGRESS }}>{active} active</span>
      {issues > 0 && <>
        <span style={{ color: '#9E9E9E' }}>|</span>
        <span style={{ color: MAP_STATUS_COLORS.ISSUE }}>{issues} issues</span>
      </>}
      {selectedCount > 0 && <>
        <span style={{ color: '#9E9E9E' }}>|</span>
        <span style={{ color: MAP_STATUS_COLORS.SELECTED }}>{selectedCount} selected</span>
      </>}
    </div>
  );
};

/**
 * MapFloatingButton — Top-right frosted glass button (Draw, Box Select, etc.)
 *
 * Props:
 *   darkMode, active, onClick, icon, label, style
 */
export const MapFloatingButton = ({ darkMode, active, onClick, icon, label, style }) => {
  const textColor = darkMode ? '#ffffff' : '#1e293b';
  return (
    <button onClick={onClick} style={{
      padding: '12px 16px', borderRadius: '12px', border: 'none',
      backgroundColor: active ? '#FFD700' : (darkMode ? 'rgba(20,20,22,0.92)' : 'rgba(255,255,255,0.95)'),
      color: active ? '#000' : textColor,
      fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer',
      boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      minHeight: '48px', minWidth: '48px',
      display: 'flex', alignItems: 'center', gap: '6px',
      ...style,
    }}>
      {icon}
      {label}
    </button>
  );
};

/**
 * QuickStatusButtons — Floating status buttons directly on the map
 *
 * Shows when segments are selected AND the toggle is ON.
 * Compact circular buttons stacked vertically on the right side of the map.
 *
 * Props:
 *   darkMode, visible, selectedCount, onStatusUpdate, updating
 */
export const QuickStatusButtons = ({ darkMode, visible, selectedCount = 0, onStatusUpdate, updating = false }) => {
  if (!visible || selectedCount === 0) return null;

  const buttons = [
    { label: 'In Progress', status: 'In Progress', color: MAP_STATUS_COLORS.IN_PROGRESS, icon: '\u25B6' },
    { label: 'Complete', status: 'Complete', color: MAP_STATUS_COLORS.COMPLETE, icon: '\u2713' },
    { label: 'Issue', status: 'Issue', color: MAP_STATUS_COLORS.ISSUE, icon: '!' },
  ];

  return (
    <div style={{
      position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)',
      display: 'flex', flexDirection: 'column', gap: '10px',
      zIndex: 15,
    }}>
      {buttons.map(btn => (
        <button
          key={btn.status}
          disabled={updating}
          onClick={() => onStatusUpdate(btn.status)}
          title={btn.label}
          style={{
            width: '52px', height: '52px', borderRadius: '50%', border: 'none',
            backgroundColor: btn.color, color: '#fff',
            fontSize: '1.3rem', fontWeight: 800, cursor: updating ? 'default' : 'pointer',
            opacity: updating ? 0.5 : 1,
            boxShadow: `0 4px 14px ${btn.color}55`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'all 0.15s',
          }}
        >
          {updating ? <Loader size={18} style={{ animation: 'spin 1s linear infinite' }} /> : btn.icon}
        </button>
      ))}
      <div style={{
        textAlign: 'center', fontSize: '0.65rem', fontWeight: 700,
        color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.5)',
        backgroundColor: darkMode ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.8)',
        borderRadius: '8px', padding: '3px 6px',
        backdropFilter: 'blur(4px)',
      }}>
        {selectedCount} sel
      </div>
    </div>
  );
};

export default MapActionBar;
